import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Drawer,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Navigate } from "react-router-dom";
import Logo from "components/styled/Logo";
import StatusBanner from "components/dashboard/StatusBanner";
import { useAuth } from "hooks/useAuth";
import {
  OnboardingDrawer,
  PatientDrawer,
  ProfessionalDrawer,
  FacilityDrawer,
  SettingsDrawer,
  AdminDrawer,
} from "./DrawerComponents";

const drawerWidth = 300;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1, 2),
  ...theme.mixins.toolbar,
}));

const DashboardNav = ({ children }) => {
  const { user, subscription, userData, activeSubscriptionRole, logout } =
    useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleLogout = async () => {
    await logout();
    return <Navigate to="/login" />;
  };

  useEffect(() => {
    const handleScroll = (e) => {
      const scrollTop = e.target.scrollTop;
      setIsScrolled(scrollTop > 10);
    };

    const mainContent = document.querySelector("[data-main-content]");
    if (mainContent) {
      mainContent.addEventListener("scroll", handleScroll);
      return () => mainContent.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const drawer = (
    <div>
      <DrawerHeader sx={{ alignItems: "center" }}>
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Logo />
        </Box>
        <IconButton
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { sm: "none" } }}
        >
          <MenuIcon />
        </IconButton>
      </DrawerHeader>
      <Divider />
      <div onClick={handleDrawerToggle}>
        {userData?.role === "facility" && user.emailVerified && (
          <FacilityDrawer
            activeSubscriptionRole={activeSubscriptionRole}
            userData={userData}
          />
        )}

        {userData?.role === "patient" && user.emailVerified && (
          <PatientDrawer subscription={subscription} userData={userData} />
        )}

        {userData?.role === "professional" && user.emailVerified && (
          <ProfessionalDrawer subscription={subscription} userData={userData} />
        )}

        {user.emailVerified ? (
          <>
            <AdminDrawer userData={userData} />
            <SettingsDrawer
              activeSubscriptionRole={activeSubscriptionRole}
              userData={userData}
            />
          </>
        ) : null}

        <OnboardingDrawer logout={handleLogout} />
      </div>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#fff",
          boxShadow: "none",
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          transition: "box-shadow 0.3s ease-in-out",
          ...(isScrolled && {
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }),
        }}
      >
        <Toolbar sx={{ display: { xs: "flex", sm: "none" } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: { sm: "none" } }}>
            <Logo />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        data-main-content
        sx={{
          flexGrow: 1,
          margin: "auto",
          // maxWidth: "1024px",
          p: 3,
          paddingTop: { xs: "65px", sm: 0 },
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
          width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
          height: "100dvh",
          overflow: "auto",
          position: "relative",
        }}
      >
        <StatusBanner />
        {children}
      </Box>
    </Box>
  );
};

export default DashboardNav;
